import { CONFIG } from '../../appConfig';

const RENTAL_TICKET = 'rentalTicket/';

export default {
  /**
   * @desc Gets route info for an upcoming rental for VMC app
   * 
   * @param {Object} data - Function parameters 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example 
   * services.gma.rentalTicket.upcomingRental()
   * returns { session, go_to } object
   */
  upcomingRental(data) {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}navigate/upcoming`,
      data: data
    };
  },

  /**
   * 
   * @desc Gets route info for rental by phone for VMC app
   * 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example 
   * services.gma.rentalTicket.rentalByPhone()
   * returns { session, go_to } object
   */
  rentalByPhone() {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}navigate/rentalByPhone`
    };
  },

  /**
   * 
   * @desc Gets route info for rental by email for VMC app
   * 
   * @param {Object} data - Function parameters 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example 
   * services.gma.rentalTicket.rentalByEmail()
   * returns { session, go_to } object
   */
  rentalByEmail(data) {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}navigate/rentalByEmail`,
      data: data
    };
  },

  /**
   * 
   * @desc Gets route info for rental by confirm number for VMC app
   * 
   * @param {Object} data - Function parameters 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example 
   * services.gma.rentalTicket.rentalByConfNumber()
   * returns { session, go_to } object
   */
  rentalByConfNumber(data) {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}navigate/rentalByConfNumber`,
      data: data
    };
  },

  /**
   * 
   * @desc Gets data for VMC app if search by phone
   * 
   * @param {Object} data - Function parameters 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example 
   * const payload = {
   *  phone:{
   *      phone_country_code:"+55",
   *       phone_number:"51995495920"
   *   },
   *   first_name:"ISOBAR",
   *   last_name:"TESTMARKLAND",
   *   verification:{
   *       token:"03AKH6MRE7XQg-mnWPdTsh7Hodw8HMI3vI9_S7EWaYDJ",
   *       key:"6Lc6K7IkAAAAAFYTmy5ouEcJjSPBIxYynh4lILQ9",
   *       process:"SendDeepLink"
   *   }
   * }
   * services.gma.rentalTicket.searchByPhone()
   * returns { session, go_to } object
   */
  searchByPhone(data) {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}sendDeepLink/phone`,
      data: data
    };
  },

  /**
   * 
   * @desc Gets data for VMC app if search by email
   * 
   * @param {Object} data - Function parameters 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * const payload = {
   *   email_address:"rodrigo.neis@gmail.com",
   *   first_name:"ISOBAR",
   *   last_name:"TESTMARKLAND",
   *   verification:{
   *       token:"03AKH6MRE7XQg-mnWPdTsh7Hodw8HMI3vI9_S7EWaYDJ",
   *       key:"6Lc6K7IkAAAAAFYTmy5ouEcJjSPBIxYynh4lILQ9",
   *       process:"SendDeepLink"
   *   }
   * }
   * services.gma.rentalTicket.searchByEmail()
   * returns { session, go_to } object
   */
  searchByEmail(data) {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}sendDeepLink/email`,
      data: data
    };
  },

  /**
   * 
   * @desc Gets data for VMC app if search by Trip Identifier
   * (Rental Agreement Number | Confirmation Number) 
   * 
   * @param {Object} data - Function parameters 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * const payload = {
   *   trip_identifierr: "3C74L3",
   *   first_name: "DRIVEALLIANCE",
   *   last_name: "LOZANO",
   *   verification:{
   *       token:"03AKH6MRE7XQg-mnWPdTsh7Hodw8HMI3vI9_S7EWaYDJ",
   *       key:"6Lc6K7IkAAAAAFYTmy5ouEcJjSPBIxYynh4lILQ9",
   *       process:"SearchTrips"
   *   }
   * }
   * 
   * services.gma.rentalTicket.searchByConfNumber()
   * returns { session, go_to } object
   */
  searchByConfNumber(data) {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}searchTrips`,
      data: data
    };
  },

  /**
   * 
   * @desc Send a confirmation email or text to verify the rental
   * 
   * @param {Object} data - Function parameters
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * const data = {
   *    "first_name":"Test",
   *    "last_name":"Testing",
   *    "trip_identifier":"1207616128",
   *    "deeplink_delivery_option":"EMAIL" || "PHONE",
   *    !!PHONE && "phone_country_code": "+1"
   *    "verification": {
   *        "token":"03AKH6MRE7XQg-mnWPdTsh7Hodw8HMI3vI9_S7EWaYDJ",
   *        "key":"6Lc6K7IkAAAAAFYTmy5ouEcJjSPBIxYynh4lILQ9",
   *        "process":"SendDeepLink"
   *    }
   * }
   * services.gma.rentalTicket.verifyByConfNumber()
   * returns { session, go_to } object
   */ 
  verifyByConfNumber(data) {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}sendDeepLink/confNumber`,
      data: data
    };
  },

  /**
   * 
   * @desc Gets data for VMC page
   * 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * services.gma.rentalTicket.bookRental()
   * returns { session, go_to } object
   * 
   */
  bookRental() {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}navigate/book`
    };
  },

  /**
   * 
   * @desc Returns Retrieve Rental data
   * 
   * @param {Object} data - Function parameters
   * @param {string} data.ticketNumber - Ticket number 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * const data = {
   *  ticketNumber: 1293823
   * }
   * services.gma.rentalTicket.retrieveTicketNumber()
   * returns { session, go_to } object
   */
  retrieveTicketNumber(data) {
    const { ticketNumber = {} } = data;

    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}retrieve/${ticketNumber}`,
      data
    };
  },
  /**
   * 
   * @desc Returns Retrieve and Check Eligibility date
   * 
   * @param {Object} data - Function parameters 
   * @param {string} data.ticketNumber - Ticket number 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * const data = {
   *  ticketNumber: 1293823
   * }
   * services.gma.rentalTicket.retrieveAndCheckEligibility()
   * returns { session, go_to } object
   */
  retrieveAndCheckEligibility(data = {}) {
    const { ticketNumber = {} } = data;
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}retrieveAndCheckEligibility/${ticketNumber}`,
      data
    };
  },
  /**
   * 
   * @desc Returns details for modify rental
   * 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * services.gma.rentalTicket.modifyDetails()
   * returns { session, go_to } object
   */
  modifyDetails() {
    return {
      method: 'GET',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}modifyDetails`
    };
  },
  /**
   * 
   * @desc Initiate modify return
   * 
   * @param {Object} data - Function parameters 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * const payload = 
   * {
   *  "return_location": {
   *     "id": "1005381",
   *    "name": "Hendersonville",
   *     "latitude": 36.303837,
   *    "longitude": -86.648554,
   *    "type": "BRANCH",
   *    "location_type": "BRANCH",
   *    "date_time": "2022-11-07T13:59",
   *    "country_code": "US"
   *  }
   * }
   * services.gma.rentalTicket.initiate({payload})
   * returns { session, go_to } object
   */
  modifyInitiate(data = {}) {
    const { payload = {}, params = {} } = data;
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}initiate`,
      data: payload,
      params
    };
  },
  /**
   * 
   * @desc Retrieves and confirm a return
   * @param {Object} data - Function parameters 
   * @param {string} data.ticketNumber - Ticket number
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * const data = {
   *  endpoint: {
   *   ticketNumber: 1293823
   *  }
   * }
   * services.gma.rentalTicket.retrieveAndConfirm(data)
   * returns { session, go_to } object
   */
  retrieveAndConfirm(data = {}) {
    const { endpoint = {} } = data;
    const { ticketNumber = "" } = endpoint;
    const queryParam = ticketNumber ? `/${ticketNumber}` : "";

    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}retrieveAndConfirm${queryParam}`
    };
  },
  /**
   * 
   * @desc Commits a return modification
   * 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * services.gma.rentalTicket.commit()
   * returns { session, go_to } object
   */
  commit() {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}commit`
    };
  },
  /**
   * 
   * @desc Confirm a return
   * 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * services.gma.rentalTicket.confirm()
   * returns { session, go_to } object
   */
  confirm() {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}confirm`
    };
  },
  /**
   * 
   * @desc Abort a modification
   * 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * services.gma.rentalTicket.abortModify()
   * returns { session, go_to } object
   */
  abortModify() {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}modify/abort`
    };
  },
  /**
   * 
   * @desc Discard modifications
   * 
   * @memberof services.gma.rentalTicket
   * 
   * @returns {Object}
   * 
   * @example
   * services.gma.rentalTicket.clearSession()
   * returns { session, go_to } object
   */
  clearSession() {
    return {
      method: 'POST',
      origin: CONFIG.GMA_ORIGIN_URL,
      endpoint: `${RENTAL_TICKET}session/clear`
    };
  }
};
